import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { FormGroup } from '@angular/forms';
import { ContainerClient } from '@azure/storage-blob';
import { BehaviorSubject, firstValueFrom, Subject, take, tap } from 'rxjs';
import { ForgeService } from 'src/app/_services/forge.service';
import { WakeLockService } from 'src/app/_services/wake-lock.service';
import { UploadContainerClientService } from 'src/app/upload/_services/upload-container-client.service';
import { UploadFailureTrackingService } from 'src/app/upload/_services/upload-failure-tracking.service';
import { Status as FileOfUploadStatus, Status, UploadFile } from 'src/app/upload/models/FileOfUpload';
import { ModifiedFile, Upload } from 'src/app/upload/models/Upload';
import { v4 as uuidv4 } from 'uuid';
import { EDataType } from '../../../upload/models/DataType';
import { CAD_EXTENSIONS, LARGE_UPLOAD_CONFIG } from '../../../upload/Utils/Constant';
import { UploadOtpService } from './upload-otp.service';
import { UploadSource } from '../../../_models/upload-source';
import { UploadActivityService } from '../../../_services/upload-activity.service';

// Enhanced upload management types
interface QueueStats {
  total: number;
  queued: number;
  uploading: number;
  completed: number;
  failed: number;
  totalSize: number;
  uploadedSize: number;
  estimatedTimeRemaining: number;
}

interface IUploadFile {
  file: ModifiedFile;
  uploadFilePath: any;
}

@Injectable({
  providedIn: 'root',
})
export class UploadFileOtpService {
  private newFilesForUpload: UploadFile[] = [];
  abortController?: AbortController;
  signal?: AbortSignal;
  isPartialUploadLoading = false;
  filesAlreadyUpload$ = new BehaviorSubject<number>(0);
  filesShouldUpload$ = new BehaviorSubject<number>(0);
  xmlFileUpload$ = new Subject<Upload>();
  isFilesUploading$ = new BehaviorSubject<boolean>(false);
  xmlFilesUploaded: Upload[] = [];
  stopUploading$ = new BehaviorSubject<boolean>(false);
  uploadedFiles$ = new BehaviorSubject<string[]>([]);
  isDeleting$ = new BehaviorSubject<boolean>(false);
  isDeleted$ = new BehaviorSubject<boolean>(false);
  uploadModelIds: string[] = [];
  isForgeProcessing$ = new BehaviorSubject<boolean>(false);

  // New properties for enhanced upload management
  queueStats$ = new BehaviorSubject<QueueStats>({
    total: 0,
    queued: 0,
    uploading: 0,
    completed: 0,
    failed: 0,
    totalSize: 0,
    uploadedSize: 0,
    estimatedTimeRemaining: 0,
  });
  memoryUsage$ = new BehaviorSubject<number>(0);
  isLargeUpload = false;
  uploadType: 'LARGE' | 'MASSIVE' = 'LARGE';

  constructor(
    private uploadService: UploadOtpService,
    private wakeLockService: WakeLockService,
    private forgeService: ForgeService,
    private toastr: ToastrService,
    private uploadContainerClientService: UploadContainerClientService,
    private uploadFailureTrackingService: UploadFailureTrackingService,
    private uploadActivity: UploadActivityService
  ) {
    this.uploadActivity.track(UploadSource.OtpUpload, this.isFilesUploading$);

    // Monitor memory usage periodically
    this.startMemoryMonitoring();
  }

  getUploadContainerClient(forceRenew = false): ContainerClient {
    return this.uploadContainerClientService.getContainerClient(forceRenew);
  }

  private async uploadBrowserData(
    file: ModifiedFile,
    uploadFilePath: any,
    containerClient: any,
    isAdditionalFiles: boolean
  ): Promise<void> {
    console.log('Start upload');
    const client = containerClient.getBlockBlobClient(
      uploadFilePath +
        '/' +
        (file.modifiedPath ? file.modifiedPath : file.webkitRelativePath === '' ? file.name : file.webkitRelativePath)
    );

    // Get optimized upload settings based on file size and upload type
    const uploadOptions = this.getOptimizedUploadSettings(file, isAdditionalFiles);

    await client.uploadBrowserData(file, uploadOptions);
  }

  private getNewFile(
    file: ModifiedFile,
    uploadFilePath: any,
    status: Status,
    uploadId: string | undefined
  ): UploadFile {
    return {
      name: file.name,
      extension: file.name.split('.').pop(),
      id: undefined,
      sizeOfFile: file.size,
      status,
      uploadedPathId: uploadFilePath,
      filePath: file.modifiedPath || file.webkitRelativePath,
      uploadFilesEntityId: uploadId,
      urn: file.urn,
    };
  }

  /**
   * @returns whether the file reached blob storage
   */
  private async uploadAdditionalFile(file: ModifiedFile, uploadFilePath: any, uploadId: string): Promise<boolean> {
    try {
      try {
        await this.uploadBrowserData(file, uploadFilePath, this.getUploadContainerClient(), true);
      } catch (error) {
        if (!this.uploadContainerClientService.isAuthenticationError(error)) {
          throw error;
        }

        // SAS token rejected, most likely expired: retry once with a new token
        await this.uploadBrowserData(file, uploadFilePath, this.getUploadContainerClient(true), true);
      }

      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext && CAD_EXTENSIONS.includes(ext)) {
        this.isForgeProcessing$.next(true);
        try {
          file.urn = await this.forgeService.uploadAndStartTranslation(file);
        } catch (err) {
          this.toastr.error('Datei konnte nicht angezeigt werden. Bitte versuchen Sie es später erneut oder wenden Sie sich an unser Support-Team.');
        } finally {
          this.isForgeProcessing$.next(false);
        }
      }
      const newFile = this.getNewFile(file, uploadFilePath, Status.Open, uploadId);
      this.newFilesForUpload.push(newFile);
      console.log('Upload add file');

      return true;
    } catch (error) {
      console.error('Azure error' + error);
      this.uploadFailureTrackingService.trackFailedFile('UploadOtp.FileFailed', file, uploadFilePath, uploadId, error);

      return false;
    }
  }

  /**
   * Uploads the additional files and registers them.
   * @returns the number of files that did not reach blob storage (failed or skipped after a stop)
   */
  async uploadAdditionalFiles(
    files: ModifiedFile[],
    uploadFilePath: any,
    uploadedCount: number,
    structuredFiles: any[],
    uploadId: string
  ): Promise<number> {
    // Determine upload type based on file count
    this.uploadType = this.determineUploadType(files.length);
    this.isLargeUpload = this.uploadType !== 'LARGE';

    console.log(`Starting upload of ${files.length} files (Type: ${this.uploadType})`);

    // Use optimized upload approach based on upload type
    const uploadedFilesCount = await this.uploadWithLegacySystem(
      files,
      uploadFilePath,
      uploadedCount,
      structuredFiles,
      uploadId
    );

    this.wakeLockService.releaseWakeLock();

    if (this.isDeleting$.value) {
      // Awaited so the caller sees isDeleted$ and skips saving an upload that no longer exists
      await firstValueFrom(this.uploadService.deleteUploadsByCode(this.uploadModelIds));
      this.isDeleted$.next(true);
      this.uploadedFiles$.next([]);
    } else {
      this.uploadedFiles$.next([]);
    }

    const notUploadedCount = files.length - uploadedFilesCount;
    if (notUploadedCount > 0) {
      console.warn(`Upload ${uploadId}: ${notUploadedCount} of ${files.length} files did not reach blob storage`);
    }

    return notUploadedCount;
  }

  async uploadMainFiles(
    xmlFiles: ModifiedFile[],
    containerClient: any,
    uploadFilePath: string,
    array: any[], // This should be additional files only, not including XML files
    uploadForm: FormGroup,
    uploadDate: Date,
    alreadyUploadedXml: Upload[],
    oldFiles?: any[],
    uploadModel?: Upload,
    structuredFiles?: ModifiedFile[]
  ): Promise<Upload> {
    try {
      this.isFilesUploading$.next(true);

      // Step 1: Upload files to blob storage
      await this.uploadFilesToBlob(xmlFiles, containerClient, uploadFilePath);

      // Step 2: Create or update upload model
      uploadModel = await this.createUploadModel(uploadForm, uploadDate, array, uploadFilePath, uploadModel);

      // Step 3: Add files to upload model
      this.addFilesToUploadModel(uploadModel, xmlFiles, oldFiles, array, uploadFilePath);

      // Step 4: Save upload to database
      const savedUpload = await this.saveUploadToDatabase(uploadModel);
      // Step 5: Process XML files with AI if needed
      await this.processXmlFilesWithAI(savedUpload, xmlFiles);

      // Step 6: Update tracking and mark as uploaded
      this.updateUploadTracking(savedUpload, alreadyUploadedXml, xmlFiles, structuredFiles);

      return savedUpload;
    } catch (error) {
      console.error('Error in uploadMainFiles:', error);
      throw error;
    } finally {
      this.isFilesUploading$.next(false);
    }
  }

  clearData(): void {
    this.newFilesForUpload = [];
    this.isFilesUploading$.next(false);
    this.filesAlreadyUpload$.next(0);
    this.filesShouldUpload$.next(0);
    this.queueStats$.next({
      total: 0,
      queued: 0,
      uploading: 0,
      completed: 0,
      failed: 0,
      totalSize: 0,
      uploadedSize: 0,
      estimatedTimeRemaining: 0,
    });
  }

  markUploaded(nodes: any[], paths: string[]) {
    const pathSet = new Set(paths); // Using a Set for O(1) lookup
    const updateNode = (node: any): void => {
      if (node.type === 'file' && pathSet.has(node.path)) {
        node.isUploaded = true;
      }
      if (node.children) {
        node.children.forEach(updateNode); // Recursively update children
      }
    };
    nodes.forEach(updateNode);
  }

  // Helper method: Upload files to blob storage
  private async uploadFilesToBlob(
    xmlFiles: ModifiedFile[],
    containerClient: any,
    uploadFilePath: string
  ): Promise<void> {
    const uploadPromises = xmlFiles.map(async (file, index) => {
      try {
        await this.uploadBrowserData(file, uploadFilePath, containerClient, false);

        // Update progress
        const currentProgress = this.filesAlreadyUpload$.getValue() + file.size;
        this.filesAlreadyUpload$.next(currentProgress);

        console.log(`Uploaded file ${index + 1}/${xmlFiles.length}: ${file.name}`);
      } catch (error) {
        console.error(`Failed to upload file ${file.name}:`, error);
        throw new Error(`Upload failed for file: ${file.name}`);
      }
    });

    await Promise.all(uploadPromises);
  }

  // Helper method: Create upload model
  private async createUploadModel(
    uploadForm: FormGroup,
    uploadDate: Date,
    array: any[],
    uploadFilePath: string,
    existingModel?: Upload
  ): Promise<Upload> {
    // If updating existing model, preserve some properties
    if (existingModel) {
      await this.updateExistingUploadModel(existingModel, array);
      return existingModel;
    }

    // Create new upload model

    const uploadModel: Upload = {
      id: uuidv4(),
      name: `${uploadForm.get('name')?.value || ''}-Extern`,
      comment: uploadForm.get('comment')?.value || '',
      contractName: uploadForm.get('contractName')?.value || '',
      type: this.parseUploadType(uploadForm.get('type')?.value),
      uploadDate: uploadDate,
      filesTryToUpload: this.extractFilePathsFromArray(array),
      uploadedPathId: uploadFilePath,
      dataType: uploadForm.get('dataType')?.value,
      status: this.determineUploadStatus(array),
      files: [],
    };

    // Add to tracking
    this.uploadModelIds.push(uploadModel.id!);

    // Add project for specific data types
    if (this.requiresProject(uploadModel.dataType!)) {
      uploadModel.project = uploadForm.get('project')?.value || '';
    }

    return uploadModel;
  }

  // Helper method: Update existing upload model
  private async updateExistingUploadModel(uploadModel: Upload, _array: any[]): Promise<void> {
    uploadModel.files?.forEach((file) => {
      file.status = FileOfUploadStatus.Uploading;
    });
  }

  // Helper method: Add files to upload model
  private addFilesToUploadModel(
    uploadModel: Upload,
    xmlFiles: ModifiedFile[],
    oldFiles: any[] | undefined,
    array: any[],
    uploadFilePath: string
  ): void {
    // Initialize files array if needed
    if (!uploadModel.files || uploadModel.files.length === 0) {
      uploadModel.files = [];
    }

    // Add new XML files
    const newFiles = xmlFiles.map((file) =>
      this.createFileFromModifiedFile(file, uploadFilePath, array, uploadModel.id!)
    );
    uploadModel.files.push(...newFiles);

    // Add old files if provided
    if (oldFiles?.length) {
      const processedOldFiles = oldFiles.map((file) => ({
        ...file,
        id: undefined, // Reset ID for new upload
      }));
      uploadModel.files.push(...processedOldFiles);
    }

    // Set file statuses
    this.setFileStatuses(uploadModel.files, array);
  }

  // Helper method: Save upload to database
  private async saveUploadToDatabase(uploadModel: Upload): Promise<Upload> {
    try {
      const savedUpload = await this.uploadService.createUpdateUpload(uploadModel).pipe(take(1)).toPromise();

      if (!savedUpload) {
        throw new Error('Failed to save upload - no data returned from server');
      }

      console.log('Upload saved successfully:', savedUpload.id);
      return savedUpload;
    } catch (error) {
      console.error('Error saving upload to database:', error);
      throw new Error('Failed to save upload to database');
    }
  }

  // Helper method: Process XML files with API
  private async processXmlFilesWithAI(upload: Upload, mainFiles?: ModifiedFile[]): Promise<void> {
    const xmlFiles = mainFiles
      ? upload.files!.filter((f) => mainFiles.some((orig) => orig.name === f.name))
      : upload.files!.filter((f) => f.extension?.toLowerCase() === 'xml');
    if (!xmlFiles?.length) {
      console.log('No files found for AI processing');
      return;
    }

    try {
      const fileIds = xmlFiles.map((f) => f.id).filter((id): id is string => id !== undefined);

      if (fileIds.length === 0) {
        console.warn('No valid file IDs found for AI processing');
        return;
      }

      await this.uploadService.sendFileToAI(fileIds, upload.type!, upload.dataType!).pipe(take(1)).toPromise();

      console.log(`Sent ${fileIds.length} XML files to AI for processing`);
    } catch (error) {
      console.error('Error sending files to AI:', error);
      // Don't throw here - AI processing failure shouldn't fail the entire upload
    }
  }

  // Helper method: Update tracking and mark as uploaded
  private updateUploadTracking(
    savedUpload: Upload,
    alreadyUploadedXml: Upload[],
    xmlFiles: ModifiedFile[],
    structuredFiles?: ModifiedFile[]
  ): void {
    // Update reactive stream
    this.xmlFileUpload$.next(savedUpload);

    // Add to already uploaded list
    alreadyUploadedXml.push(savedUpload);

    // Mark files as uploaded
    const filePaths = xmlFiles.map((file) => file.modifiedPath || file.webkitRelativePath);
    this.markUploaded(structuredFiles || [], filePaths);

    console.log(`Upload tracking updated for ${xmlFiles.length} files`);
  }

  // Utility methods
  private parseUploadType(typeValue: any): number | null {
    return typeValue !== null ? Number(typeValue) : null;
  }

  private extractFilePathsFromArray(array: any[]): string[] {
    const paths = array.map((x) => x.modifiedPath || (x.webkitRelativePath === '' ? x.name : x.webkitRelativePath));
    // Remove duplicates using Set
    return [...new Set(paths)];
  }

  private determineUploadStatus(array: any[]): Status {
    return array.length === 0 ? Status.Open : Status.Partially;
  }

  private requiresProject(dataType: number): boolean {
    return dataType === +EDataType.Construction || dataType === +EDataType.Hydrodynamic;
  }

  private createFileFromModifiedFile(file: ModifiedFile, uploadFilePath: string, array: any[], uploadId: string): any {
    return this.getNewFile(file, uploadFilePath, this.determineUploadStatus(array), uploadId);
  }

  private setFileStatuses(files: any[], array: any[]): void {
    const status = array.length > 0 ? FileOfUploadStatus.Uploading : FileOfUploadStatus.Open;

    files.forEach((file) => {
      file.status = status;
    });
  }

  /**
   * Determine upload type based on file count
   */
  private determineUploadType(fileCount: number): 'LARGE' | 'MASSIVE' {
    if (fileCount >= LARGE_UPLOAD_CONFIG.MASSIVE_FILE_COUNT_THRESHOLD) {
      return 'MASSIVE';
    } else if (fileCount >= LARGE_UPLOAD_CONFIG.LARGE_FILE_COUNT_THRESHOLD) {
      return 'LARGE';
    }
    return 'LARGE';
  }

  /**
   * Get optimized upload settings based on file size and upload type
   */
  private getOptimizedUploadSettings(file: ModifiedFile, isAdditionalFiles: boolean): any {
    const fileSizeMB = file.size / (1024 * 1024);
    let blockSize: number;
    let concurrency: number;

    // Dynamic block size based on file size
    if (fileSizeMB < LARGE_UPLOAD_CONFIG.LARGE_FILE_SIZE_MB) {
      blockSize = LARGE_UPLOAD_CONFIG.AZURE_CONFIG.BLOCK_SIZE_MIN;
      concurrency = LARGE_UPLOAD_CONFIG.AZURE_CONFIG.CONCURRENCY_PER_FILE.LARGE;
    } else if (fileSizeMB < LARGE_UPLOAD_CONFIG.MASSIVE_FILE_SIZE_MB) {
      blockSize = 2 * 1024 * 1024; // 2MB for medium files
      concurrency = LARGE_UPLOAD_CONFIG.AZURE_CONFIG.CONCURRENCY_PER_FILE.LARGE;
    } else {
      blockSize = LARGE_UPLOAD_CONFIG.AZURE_CONFIG.BLOCK_SIZE_MAX;
      concurrency = LARGE_UPLOAD_CONFIG.AZURE_CONFIG.CONCURRENCY_PER_FILE.MASSIVE;
    }

    // Further reduce concurrency for large batch uploads
    if (this.isLargeUpload) {
      concurrency = Math.max(1, Math.floor(concurrency / 2));
    }

    const options: any = {
      blockSize,
      concurrency,
      blobHTTPHeaders: { blobContentType: file.type },
      timeout: LARGE_UPLOAD_CONFIG.UPLOAD_TIMEOUT_MS,
    };

    if (isAdditionalFiles && this.signal) {
      options.abortSignal = this.signal;
    }

    return options;
  }

  /**
   * Upload files using legacy system (optimized for small uploads)
   */
  /**
   * @returns the number of files that reached blob storage
   */
  private async uploadWithLegacySystem(
    files: ModifiedFile[],
    uploadFilePath: any,
    uploadedCount: number,
    structuredFiles: any[],
    uploadId: string
  ): Promise<number> {
    this.isFilesUploading$.next(true);
    const arrayAdditionalFiles = files.sort((a, b) => a.size - b.size);
    const promisesArray: IUploadFile[] = [];
    const promisesArrayOfArray = [];
    this.newFilesForUpload = [];
    let uploadedFilesCount = 0;

    for (const file of arrayAdditionalFiles) {
      promisesArray.push({ file, uploadFilePath });
    }

    // Use smaller batch sizes for better memory management
    const batchSize = LARGE_UPLOAD_CONFIG.BATCH_SIZE[this.uploadType];
    const constantValue = Math.min(3221225472, 1073741824); // Reduced from 3GB to 1GB
    let currentPart: any[] = [];
    let currentPartSize = 0;

    for (const file of promisesArray) {
      if (currentPartSize + file.file.size <= constantValue && currentPart.length < batchSize) {
        currentPart.push(file);
        currentPartSize += file.file.size;
      } else {
        promisesArrayOfArray.push(currentPart);
        currentPart = [file];
        currentPartSize = file.file.size;
      }
    }

    if (currentPart.length > 0) {
      promisesArrayOfArray.push(currentPart);
    }

    // Save files to database more frequently for small uploads
    let saveUploadFilesIndexes = [];
    const saveInterval = Math.max(1, Math.floor(promisesArrayOfArray.length / 5));
    for (let i = saveInterval; i <= promisesArrayOfArray.length; i += saveInterval) {
      saveUploadFilesIndexes.push(i - 1);
    }
    saveUploadFilesIndexes = [...new Set(saveUploadFilesIndexes)];

    this.abortController = new AbortController();
    this.signal = this.abortController.signal;

    for (const [index, ar] of promisesArrayOfArray.entries()) {
      if (this.stopUploading$.value) {
        break;
      }

      // Add delay between batches to prevent overwhelming
      if (index > 0) {
        await this.delay(LARGE_UPLOAD_CONFIG.BATCH_DELAY_MS[this.uploadType]);
      }

      const uploadResults: boolean[] = await Promise.all(
        ar.map((item: IUploadFile) => this.uploadAdditionalFile(item.file, item.uploadFilePath, uploadId))
      ).catch(() => {
        this.toastr.error('Der Upload wurde unterbrochen. Versuchen Sie bitte, die Dateien noch einmal hochzuladen.');
        return [];
      });
      uploadedFilesCount += uploadResults.filter((isUploaded) => isUploaded).length;

      if (!this.stopUploading$.value) {
        const filesSize = ar.reduce((partialSum, a) => partialSum + a.file.size, 0);
        this.filesAlreadyUpload$.next(this.filesAlreadyUpload$.getValue() + filesSize);

        if (saveUploadFilesIndexes.includes(index) || index === promisesArrayOfArray.length - 1) {
          await this.uploadService
            .uploadFiles(this.newFilesForUpload)
            .pipe(
              take(1),
              tap(() => (this.newFilesForUpload = []))
            )
            .toPromise();
        }

        const uploadedFiles = this.uploadedFiles$.getValue();
        this.markUploaded(structuredFiles, uploadedFiles);
        // Only files that reached storage get the "uploaded" mark in the file tree
        const newUploadedFiles = ar
          .filter((_: IUploadFile, itemIndex: number) => uploadResults[itemIndex])
          .map((item: IUploadFile) => item.file.modifiedPath || (item.file.webkitRelativePath as string));
        uploadedFiles.push(...newUploadedFiles);
        this.uploadedFiles$.next(uploadedFiles);
      }
    }

    if (this.newFilesForUpload.length) {
      await this.uploadService.uploadFiles(this.newFilesForUpload).pipe(take(1)).toPromise();
    }

    return uploadedFilesCount;
  }

  /**
   * Start memory monitoring
   */
  private startMemoryMonitoring(): void {
    setInterval(() => {
      if ('memory' in performance) {
        const memoryInfo = (performance as any).memory;
        const usedMemoryMB = memoryInfo.usedJSHeapSize / (1024 * 1024);
        this.memoryUsage$.next(Math.round(usedMemoryMB));

        // Warn if memory usage is high
        if (usedMemoryMB > LARGE_UPLOAD_CONFIG.MEMORY_THRESHOLD_MB * 0.8) {
          console.warn(`High memory usage detected: ${Math.round(usedMemoryMB)}MB`);
        }
      }
    }, LARGE_UPLOAD_CONFIG.MEMORY_CHECK_INTERVAL_MS);
  }

  /**
   * Pause upload processing
   */
  pauseUploads(): void {
    this.stopUploading$.next(true);
  }

  /**
   * Resume upload processing
   */
  resumeUploads(): void {
    this.stopUploading$.next(false);
  }

  /**
   * Get detailed upload statistics
   */
  getUploadStatistics(): any {
    const stats = this.queueStats$.value;
    return {
      ...stats,
      uploadType: this.uploadType,
      isLargeUpload: this.isLargeUpload,
      memoryUsageMB: this.memoryUsage$.value,
      progressPercent: stats.totalSize > 0 ? (stats.uploadedSize / stats.totalSize) * 100 : 0,
    };
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
